import React, {useMemo, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {
  clampPeriod,
  isoMonth,
  matchPreset,
  PERIOD_PRESETS,
  periodBounds,
  periodFromPreset,
  periodLength,
  shiftMonth,
  validatePeriod,
  type Period,
  type PeriodPresetId,
} from '@/entities/attendance/period';
import {humanDate as human, usePeriodLabel} from '../lib/period-label';
import {Banner} from '@/shared/ui/banner';
import {IconButton} from '@/shared/ui/button';
import {HelpNote} from '@/shared/ui/explain';
import {DateField} from '@/shared/ui/date-field';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * The control every report is steered by: which days are we looking at.
 *
 * Three ways to say it, because three different questions get asked of these reports
 * and no single control answers all of them:
 *
 *   PRESETS   — «сегодня», «неделя», «этот месяц», «прошлый месяц»… the answer to
 *               «как дела», which is most of the traffic and should be one tap.
 *   MONTH     — ‹ Август 2026 › steppers. Payroll is monthly; walking back through
 *               months is the second most common thing anyone does here, and doing it
 *               with two date fields is four interactions instead of one.
 *   RANGE     — two calendar fields, for the question nobody anticipated.
 *
 * They are not three modes the user has to choose between first: tapping a preset that
 * happens to be a whole month leaves the month stepper on the SAME month, and stepping
 * the month simply un-highlights the preset. The state is one `Period`; these are three
 * views of it.
 *
 * The 366-day ceiling is the server's, and it is enforced before the request: the
 * banner explains the refusal, and `clampPeriod` offers the nearest legal window rather
 * than leaving the user to work it out from a 422.
 *
 * It is not a card on the page any more: it lives in the sheet the period pill in
 * `ReportToolbar` opens. A report screen that opened with two hundred points of
 * controls before a single number was a query builder, not a report.
 *
 * Whichever of the three was used, the footer states the window as two dates. «Квартал»
 * and «Август 2026» are both friendly and both ambiguous about their edges, and the
 * edges are exactly what an argument over a report turns on — so the card always says,
 * in one line, which days it asked the server for.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  presets: {flexDirection: 'row', gap: spacing(2), paddingRight: spacing(4)},
  chip: {
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(2),
    borderRadius: radii.md,
    borderWidth: 1,
  },
  monthRow: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  monthLabel: {flex: 1, textAlign: 'center'},
  flip: {transform: [{rotate: '180deg'}]},
  rangeRow: {flexDirection: 'row', gap: spacing(3)},
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(3),
    flexWrap: 'wrap',
  },
  footerFacts: {flex: 1, minWidth: 180, gap: 1},
  toggle: {paddingVertical: spacing(1)},
});

export function PeriodPicker({
  period,
  onChange,
  /** Pinned once by the screen, so "today" cannot move under a render. */
  now,
}: {
  period: Period;
  onChange: (period: Period) => void;
  now: Date;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const {month: monthLabel} = usePeriodLabel();

  // The custom-range fields stay hidden until asked for: on a phone, two date pickers
  // above every report is most of the first screen spent on a control most visits
  // never touch.
  const [showRange, setShowRange] = useState(period.kind === 'range');

  const activePreset = useMemo(() => matchPreset(period, now), [period, now]);
  const bounds = periodBounds(period);
  const problem = validatePeriod(period);
  const thisMonth = isoMonth(now);

  // Stepping months works from whatever the period currently covers — including a
  // range, where the month containing its first day is the sensible starting point.
  const anchorMonth = period.kind === 'month' ? period.month : bounds.from.slice(0, 7);
  const canStepForward = anchorMonth < thisMonth;

  const setPreset = (id: PeriodPresetId) => onChange(periodFromPreset(id, now));

  const stepMonth = (delta: number) => {
    const next = shiftMonth(anchorMonth, delta);
    if (delta > 0 && next > thisMonth) {
      return;
    }
    onChange({kind: 'month', month: next});
  };

  const setBound = (which: 'from' | 'to', value: string) => {
    onChange({
      kind: 'range',
      from: which === 'from' ? value : bounds.from,
      to: which === 'to' ? value : bounds.to,
    });
  };

  return (
    <View style={styles.root} testID="period-picker">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.presets}>
        {PERIOD_PRESETS.map(id => {
          const active = activePreset === id;
          return (
            <Pressable
              key={id}
              testID={`period-preset-${id}`}
              accessibilityRole="button"
              accessibilityState={{selected: active}}
              onPress={() => setPreset(id)}
              style={({pressed}) => [
                styles.chip,
                {
                  backgroundColor: active ? p.accent : pressed ? p.muted : p.card,
                  borderColor: active ? p.primary : p.border,
                },
              ]}>
              <AppText
                variant="label"
                color={active ? p.accentForeground : p.foreground}>
                {t(`reports.preset.${id}`)}
              </AppText>
            </Pressable>
          );
        })}
      </ScrollView>

      <View style={styles.monthRow}>
        <IconButton
          icon="chevron"
          tone="ghost"
          label={t('attendance.prevMonth')}
          onPress={() => stepMonth(-1)}
          style={styles.flip}
          testID="period-month-prev"
        />
        <AppText variant="heading" style={styles.monthLabel}>
          {period.kind === 'month'
            ? monthLabel(period.month)
            : `${human(bounds.from)} — ${human(bounds.to)}`}
        </AppText>
        <IconButton
          icon="chevron"
          tone="ghost"
          label={t('attendance.nextMonth')}
          onPress={() => stepMonth(1)}
          disabled={!canStepForward}
          testID="period-month-next"
        />
      </View>

      {showRange ? (
        <View style={styles.rangeRow}>
          <DateField
            label={t('reports.from')}
            value={bounds.from}
            maximumDate={now}
            onChange={value => setBound('from', value)}
            testID="period-from"
          />
          <DateField
            label={t('reports.to')}
            value={bounds.to}
            maximumDate={now}
            onChange={value => setBound('to', value)}
            testID="period-to"
          />
        </View>
      ) : null}

      {problem ? (
        <Banner
          tone="warning"
          title={t(`reports.periodProblem.${problem}`)}
          message={t('reports.periodProblemFix')}
          testID="period-problem"
        />
      ) : null}

      <View style={styles.footer}>
        {/* The exact window, always — a preset chip and a month name both leave "so
            which days is that, exactly?" unanswered, and that is the question every
            argument about a report starts from. */}
        <View style={styles.footerFacts}>
          <AppText variant="label" color={p.foreground}>
            {`${human(bounds.from)} — ${human(bounds.to)}`}
          </AppText>
          <AppText variant="caption">
            {t('reports.dayCount', {count: periodLength(clampPeriod(period))})}
          </AppText>
        </View>
        <Pressable
          accessibilityRole="button"
          onPress={() => setShowRange(v => !v)}
          hitSlop={8}
          style={styles.toggle}
          testID="period-toggle-range">
          <AppText variant="label" color={p.primary}>
            {t(showRange ? 'reports.hideRange' : 'reports.customRange')}
          </AppText>
        </Pressable>
      </View>

      <HelpNote text={t('reports.periodHint')} />
    </View>
  );
}
